<template>
  <FormSection>
    <Card>
      <ElForm
        ref="accountInformation"
        autocomplete="off"
        :model="accountInformationModel"
        :rules="rules"
        label-position="top"
        @submit.native.prevent="submitAccountInformationForm"
      >
        <ElFormItem :label="$t('EMAIL')" prop="email">
          <ElInput
            :value="email"
            dir="ltr"
            :placeholder="$t('EMAIL')"
            type="email"
            name="email"
            disabled
          />
          <FieldError :errors="errors" field="email" />
        </ElFormItem>

        <ElFormItem :label="$t('PUBLISHER_ACCOUNT.FULL_NAME')" prop="name">
          <ElInput
            v-model="accountInformationModel.name"
            dir="ltr"
            name="name"
          />
          <FieldError :errors="errors" field="name" />
          <p class="el-form-item__help">
            <i class="el-icon-warning" />
            {{ $t('AUTH.STEPS.NAME_WARNING') }}
          </p>
        </ElFormItem>

        <ElFormItem :label="$t('MOBILE_PHONE')" prop="phone">
          <ElInput
            v-model="accountInformationModel.phone"
            type="tel"
            dir="ltr"
            name="phone"
            placeholder="00989121234567"
            :maxlength="20"
          />

          <FieldError :errors="errors" field="phone" />
          <p class="el-form-item__help">
            <i class="el-icon-warning" />
            {{ $t('AUTH.STEPS.CELLPHONE_WARNING') }}
          </p>
        </ElFormItem>

        <ElFormItem :label="$t('CITY')" prop="city" required>
          <ElInput v-model="accountInformationModel.city" name="city" />
          <FieldError :errors="errors" field="city" />
        </ElFormItem>

        <ElFormItem :label="$t('ADDRESS')" prop="address">
          <ElInput
            v-model="accountInformationModel.address"
            :autosize="{ minRows: 2, maxRows: 4 }"
            name="address"
            dir="ltr"
            :maxlength="150"
            type="textarea"
          />
          <FieldError :errors="errors" field="address" />
        </ElFormItem>

        <ElFormItem
          :label="$t('PUBLISHER_ACCOUNT.PASSPORT_CODE')"
          prop="passport_code"
        >
          <ElInput
            v-model="accountInformationModel.passport_code"
            name="passport_code"
            dir="ltr"
            :maxlength="12"
          />
          <FieldError :errors="errors" field="passport_code" />
        </ElFormItem>

        <ElFormItem
          :label="$t('PUBLISHER_ACCOUNT.PASSPORT_IMAGE')"
          prop="passport_image"
        >
          <Droppable
            :has-preview="true"
            :has-progress="true"
            file-types="image/*"
            :upload-url="uploadPaths.passport"
            @uploaded="uploadPassportImage"
          >
            <div slot="label">
              {{ $t('PUBLISHER_ACCOUNT.DRAG_PASSPORT_IMAGE') }}
            </div>
          </Droppable>
          <FieldError :errors="errors" field="passport_image" />
        </ElFormItem>

        <div class="auth-steps__navigation">
          <ElButton type="default" @click.native="handlePrevPage">
            {{ $t('AUTH.STEPS.PREV_LEVEL_BTN') }}
          </ElButton>
          <ElButton type="primary" native-type="submit" :loading="loading">
            {{ $t('AUTH.STEPS.NEXT_LEVEL_BTN') }}
          </ElButton>
        </div>

        <ErrorsList v-if="errors" :errors="errors" />
      </ElForm>
    </Card>
  </FormSection>
</template>

<script>
import normalizeNumbers from '@/utils/normalizeNumbers';

import Droppable from '@@/layout/Droppable';

import form from '@/mixins/form';

import { Publishers } from '@/api/api';
import ErrorsList from '@@/common/ErrorsList';
import FieldError from '@@/common/FieldError';
import FormSection from '@@/common/layout/FormSection';
import Card from '@@/common/Card';

export default {
  name: 'ForeignerNatural',

  components: {
    FieldError,
    ErrorsList,
    Droppable,
    FormSection,
    Card,
  },

  mixins: [form],

  props: {
    email: {
      type: String,
      required: true,
    },
    errors: {
      type: Object,
      default: null,
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      accountInformationModel: {
        name: null,
        passport_code: null,
        passport_image: {
          id: null,
          url: null,
        },
        address: null,
        city: null,
        phone: null,
      },

      uploadPaths: Publishers.uploadPaths,
    };
  },

  computed: {
    rules() {
      const rules = {
        required: true,
        message: this.$t('VALIDATION_REQUIRED_ERR'),
        trigger: 'blur',
      };

      return {
        name: {
          ...rules,
          type: 'string',
        },
        phone: [
          rules,
          {
            pattern: /^\d{3,20}$/i,
            message: this.$t('PUBLISHER_ACCOUNT.INVALID_FOREIGN_PHONE'),
            trigger: 'blur',
          },
        ],
        address: {
          ...rules,
          type: 'string',
        },
        city: {
          ...rules,
          type: 'string',
        },
        passport_code: [
          rules,
          {
            pattern: /^\S{8,12}$/i,
            message: this.$t('PUBLISHER_ACCOUNT.INVALID_PASSPORT_CODE'),
            trigger: 'blur',
          },
        ],
        passport_image: rules,
      };
    },
  },

  watch: {
    accountInformationModel: {
      deep: true,
      handler: normalizeNumbers,
    },
  },

  methods: {
    submitAccountInformationForm() {
      if (!this.isValid('accountInformation')) {
        return false;
      }
      this.$emit('submitAccountInformation', this.accountInformationModel);
    },

    handlePrevPage() {
      return this.$emit('prevStep');
    },

    uploadPassportImage(src) {
      this.accountInformationModel.passport_image.id = src;
    },
  },
};
</script>
